import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CropIcon from '@mui/icons-material/Crop';
import { useGetIdentity, useNotify } from 'react-admin';

import { formatDate } from '@/utils/date_utils';

import {
    type ExpenseTransaction,
    type Receipt,
    type ReceiptCategory,
    type ResponsibleUser,
    extractReceipt,
    fetchReceipts,
} from './receiptApi';
import { ProjectSearchAutocomplete } from './ProjectSearchAutocomplete';
import { ReceiptAllocationEditDialog } from './ReceiptAllocationEditDialog';
import { ReceiptPreviewDialog } from './ReceiptPreviewDialog';
import {
    ReceiptImageCropDialog,
    type ReceiptCropItem,
} from './ReceiptImageCropDialog';
import {
    projectClassEmployeeNote,
    statusColor,
    type SimpleRecord,
} from './receiptHelpers';
import { receiptUploadQueue } from './receiptUploadQueue';
import type { PendingReceiptRecord } from './receiptUploadStore';
import { compressReceiptImages } from './compressReceiptImage';

const ACCEPT_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
    'application/pdf',
]);

type PreviewItem = { file: File; url: string };

const EMPTY_CROP_ITEMS: ReceiptCropItem[] = [];

const isCroppableImage = (file: File) =>
    file.type.startsWith('image/') ||
    /\.(jpe?g|png|heic|webp)$/i.test(file.name);

type CropSession =
    | {
          mode: 'intake';
          items: ReceiptCropItem[];
          /** Full accepted selection; image slots filled after crop confirm */
          pendingFiles: File[];
          imageIndices: number[];
      }
    | {
          mode: 'edit';
          items: ReceiptCropItem[];
          editIndex: number;
      };

type Props = {
    categories: ReceiptCategory[];
    users: ResponsibleUser[];
    requestedTransaction: ExpenseTransaction | null;
    busy: boolean;
    onUploaded: () => void;
    onViewMyReceipts: () => void;
};

export const ReceiptUploadForm = ({
    categories,
    users,
    requestedTransaction,
    busy,
    onUploaded,
    onViewMyReceipts,
}: Props) => {
    const notify = useNotify();
    const { identity } = useGetIdentity();
    const [files, setFiles] = useState<PreviewItem[]>([]);
    const [allocationType, setAllocationType] = useState<
        '' | 'project_cost' | 'company_expense'
    >('');
    const [projectId, setProjectId] = useState('');
    const [selectedProject, setSelectedProject] = useState<SimpleRecord | null>(null);
    const [projectNo, setProjectNo] = useState<number | null>(null);
    const [category, setCategory] = useState('');
    const [subcategory, setSubcategory] = useState('');
    const [merchant, setMerchant] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [responsibleUserId, setResponsibleUserId] = useState('');
    const [extracting, setExtracting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [scanMessage, setScanMessage] = useState<{
        severity: 'success' | 'warning';
        text: string;
    } | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [preview, setPreview] = useState<Receipt | null>(null);
    const [editing, setEditing] = useState<Receipt | null>(null);
    const [pendingLocal, setPendingLocal] = useState<PendingReceiptRecord[]>(
        []
    );
    const [pausedForAuth, setPausedForAuth] = useState(false);
    const [uploadingLocalId, setUploadingLocalId] = useState<string | null>(
        null
    );
    const [recent, setRecent] = useState<Receipt[]>([]);
    const [serverListUnavailable, setServerListUnavailable] = useState(false);
    const [cropSession, setCropSession] = useState<CropSession | null>(null);

    const refetchRecent = useCallback(async () => {
        try {
            const rows = await fetchReceipts({ mine: true, deletedAt: null });
            setRecent(rows.slice(0, 5));
            setServerListUnavailable(false);
        } catch {
            // Offline / API down — keep local Pending rows; don't throw to console.
            setServerListUnavailable(true);
        }
    }, []);

    const selectedCategory = useMemo(
        () => categories.find(item => item.name === category),
        [categories, category]
    );

    const suggestDescription = (merchant_val: string, category_val: string): string => {
        const source = merchant_val || category_val || '';
        if (!source) return '';
        return source.substring(0, 20);
    };

    useEffect(() => {
        if (identity?.id == null) return;
        setResponsibleUserId(String(identity.id));
    }, [identity?.id]);

    useEffect(() => {
        void refetchRecent();
        const onOnline = () => {
            void refetchRecent();
            void receiptUploadQueue.kick();
        };
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
    }, [refetchRecent]);

    useEffect(() => {
        const userId =
            identity?.id == null ? null : Number(identity.id);
        void receiptUploadQueue.setUserId(
            userId != null && Number.isFinite(userId) ? userId : null
        );
        let prevCount = -1;
        return receiptUploadQueue.subscribe(snapshot => {
            if (prevCount > 0 && snapshot.pending.length < prevCount) {
                void refetchRecent();
                onUploaded();
            }
            prevCount = snapshot.pending.length;
            setPendingLocal(snapshot.pending);
            setPausedForAuth(snapshot.pausedForAuth);
            setUploadingLocalId(snapshot.uploadingLocalId);
        });
    }, [identity?.id, onUploaded, refetchRecent]);

    useEffect(
        () => () => {
            files.forEach(item => URL.revokeObjectURL(item.url));
        },
        // only revoke on unmount of current preview set when replaced
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const acceptFile = (file: File) =>
        ACCEPT_TYPES.has(file.type) ||
        /\.(jpe?g|png|heic|webp|pdf)$/i.test(file.name);

    const closeCropSession = () => {
        setCropSession(current => {
            if (current) {
                current.items.forEach(item => URL.revokeObjectURL(item.imageUrl));
            }
            return null;
        });
    };

    const applyExtractedFields = (
        result: Awaited<ReturnType<typeof extractReceipt>>,
        successText: string
    ) => {
        setMerchant(result.merchant || '');
        setAmount(result.amount != null ? String(result.amount) : '');
        setTransactionDate(result.transaction_date || '');
        setAllocationType(result.project_id ? 'project_cost' : 'company_expense');
        setCategory(result.project_id ? '' : result.category || '');
        setSubcategory(result.project_id ? '' : result.subcategory || '');
        setProjectId(result.project_id ? String(result.project_id) : '');
        setProjectNo(null);
        const newMerchant = result.merchant || '';
        const newCategory = result.project_id ? '' : result.category || '';
        setDescription(suggestDescription(newMerchant, newCategory));
        setScanMessage({ severity: 'success', text: successText });
    };

    const ingestFiles = async (
        incoming: File[],
        successText: string,
        replaceAtIndex?: number
    ) => {
        const compressed = await compressReceiptImages(incoming);
        if (replaceAtIndex != null) {
            const newFiles = [...files];
            const oldItem = newFiles[replaceAtIndex];
            if (oldItem) URL.revokeObjectURL(oldItem.url);
            newFiles[replaceAtIndex] = {
                file: compressed[0],
                url: URL.createObjectURL(compressed[0]),
            };
            setFiles(newFiles);
            setExtracting(true);
            try {
                const result = await extractReceipt(newFiles.map(item => item.file));
                applyExtractedFields(result, successText);
            } catch (error) {
                setScanMessage({
                    severity: 'warning',
                    text:
                        error instanceof Error
                            ? `${error.message} You can still enter the fields manually.`
                            : 'Receipt scanning failed. You can still enter the fields manually.',
                });
            } finally {
                setExtracting(false);
            }
            return;
        }

        const nextPreviews = compressed.map(file => ({
            file,
            url: URL.createObjectURL(file),
        }));
        const allFiles = [...files.map(item => item.file), ...compressed];
        setFiles(current => [...current, ...nextPreviews]);
        setScanMessage(null);

        setExtracting(true);
        try {
            const result = await extractReceipt(allFiles);
            applyExtractedFields(result, successText);
        } catch (error) {
            setScanMessage({
                severity: 'warning',
                text:
                    error instanceof Error
                        ? `${error.message} You can still enter the fields manually.`
                        : 'Receipt scanning failed. You can still enter the fields manually.',
            });
        } finally {
            setExtracting(false);
        }
    };

    const addFiles = async (selected: FileList | File[] | null) => {
        if (!selected?.length) return;
        const nextFiles = Array.from(selected).filter(acceptFile);
        if (!nextFiles.length) {
            setScanMessage({
                severity: 'warning',
                text: 'Use JPEG, PNG, HEIC, WebP, or PDF files.',
            });
            return;
        }

        const imageIndices = nextFiles
            .map((file, index) => (isCroppableImage(file) ? index : -1))
            .filter(index => index >= 0);

        if (!imageIndices.length) {
            await ingestFiles(
                nextFiles,
                nextFiles.length > 1
                    ? 'GPT-4o scanned the joined receipt pages. Review the suggested fields before uploading.'
                    : 'GPT-4o scanned the receipt. Review the suggested fields before uploading.'
            );
            return;
        }

        // Crop images first; PDFs and other non-images stay in pendingFiles slots.
        setCropSession({
            mode: 'intake',
            items: imageIndices.map(index => {
                const file = nextFiles[index];
                return { file, imageUrl: URL.createObjectURL(file) };
            }),
            pendingFiles: nextFiles,
            imageIndices,
        });
    };

    const removeFile = (index: number) => {
        setFiles(current => {
            const target = current[index];
            if (target) URL.revokeObjectURL(target.url);
            return current.filter((_, i) => i !== index);
        });
    };

    const handleCropConfirm = async (croppedFiles: File[]) => {
        const session = cropSession;
        closeCropSession();
        if (!session) return;

        if (session.mode === 'edit') {
            await ingestFiles(
                [croppedFiles[0]],
                'Cropped image scanned. Review the fields before uploading.',
                session.editIndex
            );
            return;
        }

        const merged = [...session.pendingFiles];
        session.imageIndices.forEach((fileIndex, cropIndex) => {
            const cropped = croppedFiles[cropIndex];
            if (cropped) merged[fileIndex] = cropped;
        });
        await ingestFiles(
            merged,
            merged.length > 1
                ? 'GPT-4o scanned the joined receipt pages. Review the suggested fields before uploading.'
                : 'GPT-4o scanned the receipt. Review the suggested fields before uploading.'
        );
    };

    const resetForm = () => {
        closeCropSession();
        files.forEach(item => URL.revokeObjectURL(item.url));
        setFiles([]);
        setAllocationType('');
        setProjectId('');
        setProjectNo(null);
        setCategory('');
        setSubcategory('');
        setMerchant('');
        setDescription('');
        setAmount('');
        setTransactionDate('');
        if (identity?.id != null) {
            setResponsibleUserId(String(identity.id));
        }
        setScanMessage(null);
    };

    const submit = async () => {
        if (
            !files.length ||
            !allocationType ||
            (allocationType === 'project_cost' && !projectId) ||
            (allocationType === 'company_expense' && !category) ||
            identity?.id == null
        ) {
            return;
        }
        const userId = Number(identity.id);
        if (!Number.isFinite(userId)) return;

        setUploading(true);
        try {
            await receiptUploadQueue.enqueue({
                userId,
                files: files.map(item => item.file),
                allocation_type: allocationType,
                project_id:
                    allocationType === 'project_cost'
                        ? Number(projectId)
                        : null,
                project_no:
                    allocationType === 'project_cost' ? projectNo : null,
                transaction_id: requestedTransaction?.id ?? null,
                category:
                    allocationType === 'company_expense' ? category : null,
                subcategory:
                    allocationType === 'company_expense'
                        ? subcategory || null
                        : null,
                merchant: merchant || null,
                amount: amount || null,
                transaction_date: transactionDate || null,
                responsible_user_id: responsibleUserId
                    ? Number(responsibleUserId)
                    : userId,
                description: description || null,
            });
            notify(
                navigator.onLine === false
                    ? 'Queued — will sync when online'
                    : 'Queued — syncing in the background',
                { type: 'success' }
            );
            resetForm();
        } catch (error) {
            notify(
                error instanceof Error
                    ? error.message
                    : 'Could not queue receipt',
                { type: 'error' }
            );
        } finally {
            setUploading(false);
        }
    };

    const disabled =
        busy ||
        uploading ||
        extracting ||
        !files.length ||
        !allocationType ||
        (allocationType === 'project_cost' && !projectId) ||
        (allocationType === 'company_expense' &&
            (!category ||
                (!!selectedCategory?.subcategories.length && !subcategory)));

    return (
        <Stack spacing={3}>
            {requestedTransaction && (
                <Alert severity="warning">
                    Receipt requested for {requestedTransaction.description} · $
                    {requestedTransaction.amount} on{' '}
                    {requestedTransaction.transaction_date}. Uploading here will
                    attach it directly to that transaction.
                </Alert>
            )}
            {pausedForAuth && pendingLocal.length > 0 && (
                <Alert severity="warning">
                    Sign in again to finish uploading {pendingLocal.length}{' '}
                    receipt{pendingLocal.length === 1 ? '' : 's'}.
                </Alert>
            )}
            {!pausedForAuth && pendingLocal.length > 0 && (
                <Alert severity="info">
                    {pendingLocal.length} receipt
                    {pendingLocal.length === 1 ? '' : 's'} waiting to sync.
                    Keep this page open or return later — retries every 5
                    minutes when online.
                </Alert>
            )}
            {serverListUnavailable && pendingLocal.length === 0 && (
                <Alert severity="info">
                    Server receipts unavailable (offline or API down). You can
                    still queue uploads — they will sync when the connection
                    returns.
                </Alert>
            )}
            <Box sx={{ position: 'relative' }}>
                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                        <Box
                            component="label"
                            onDragEnter={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDragActive(true);
                            }}
                            onDragOver={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDragActive(true);
                            }}
                            onDragLeave={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (event.currentTarget.contains(event.relatedTarget as Node)) {
                                    return;
                                }
                                setDragActive(false);
                            }}
                            onDrop={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setDragActive(false);
                                void addFiles(event.dataTransfer.files);
                            }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                minHeight: 180,
                                border: '2px dashed',
                                borderColor: dragActive ? 'primary.main' : 'divider',
                                borderRadius: 2,
                                bgcolor: dragActive ? 'action.selected' : 'action.hover',
                                cursor: 'pointer',
                                px: 2,
                                py: 3,
                                '&:hover': { borderColor: 'primary.main' },
                            }}
                        >
                            <UploadFileIcon color="action" sx={{ fontSize: 40 }} />
                            <Typography>
                                Drop receipt photos here, or click to choose
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                JPEG, PNG, HEIC, WebP, or PDF · multiple files OK
                            </Typography>
                            <input
                                hidden
                                multiple
                                type="file"
                                accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
                                onChange={event => {
                                    void addFiles(event.target.files);
                                    event.target.value = '';
                                }}
                            />
                        </Box>
                        {!!files.length && (
                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {files.map((item, index) => (
                                    <Card
                                        key={`${item.file.name}-${index}`}
                                        variant="outlined"
                                        sx={{ width: 120, position: 'relative' }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={0.25}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 2,
                                                zIndex: 1,
                                                columnGap: '7px'
                                            }}
                                        >
                                            {item.file.type.startsWith('image/') && (
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Crop ${item.file.name}`}
                                                    onClick={() => {
                                                        setCropSession({
                                                            mode: 'edit',
                                                            items: [
                                                                {
                                                                    file: item.file,
                                                                    imageUrl:
                                                                        URL.createObjectURL(
                                                                            item.file
                                                                        ),
                                                                },
                                                            ],
                                                            editIndex: index,
                                                        });
                                                    }}
                                                    sx={{
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    <CropIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                aria-label={`Remove ${item.file.name}`}
                                                onClick={() => removeFile(index)}
                                                sx={{
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                        {item.file.type.startsWith('image/') ? (
                                            <Box
                                                component="img"
                                                src={item.url}
                                                alt={item.file.name}
                                                sx={{
                                                    width: '100%',
                                                    height: 88,
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 88,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    px: 1,
                                                }}
                                            >
                                                <Typography variant="caption" align="center">
                                                    PDF
                                                </Typography>
                                            </Box>
                                        )}
                                        <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ display: 'block', px: 0.75, py: 0.5 }}
                                        >
                                            {item.file.name}
                                        </Typography>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                        {extracting && (
                            <Alert severity="info">Scanning first receipt with GPT-4o…</Alert>
                        )}
                        {scanMessage && (
                            <Alert severity={scanMessage.severity}>
                                {scanMessage.text}
                            </Alert>
                        )}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <FormControl fullWidth required>
                                <InputLabel>Allocation type</InputLabel>
                                <Select
                                    value={allocationType}
                                    label="Allocation type"
                                    onChange={event => {
                                        const next = event.target.value as
                                            | 'project_cost'
                                            | 'company_expense';
                                        setAllocationType(next);
                                        setProjectId('');
                                        setProjectNo(null);
                                        setCategory('');
                                        setSubcategory('');
                                    }}
                                >
                                    <MenuItem value="project_cost">
                                        Project cost (COGS)
                                    </MenuItem>
                                    <MenuItem value="company_expense">
                                        Company expense
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {allocationType === 'project_cost' && (
                                <ProjectSearchAutocomplete
                                    value={projectId}
                                    onChange={setProjectId}
                                    onProjectSelect={project => {
                                        setProjectNo(project?.project_no ?? null);
                                        setSelectedProject(project);
                                    }}
                                    required
                                />
                            )}
                            {allocationType === 'project_cost' &&
                                projectClassEmployeeNote(selectedProject) && (
                                    <Alert severity="info">
                                        {projectClassEmployeeNote(selectedProject)}
                                    </Alert>
                                )}
                            {allocationType === 'company_expense' && (
                                <FormControl fullWidth required>
                                    <InputLabel>Expense category</InputLabel>
                                    <Select
                                        value={category}
                                        label="Expense category"
                                        onChange={event => {
                                            setCategory(event.target.value);
                                            setSubcategory('');
                                        }}
                                    >
                                        {categories.map(item => (
                                            <MenuItem key={item.name} value={item.name}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {allocationType === 'company_expense' &&
                                !!selectedCategory?.subcategories.length && (
                                    <FormControl fullWidth required>
                                        <InputLabel>Expense subcategory</InputLabel>
                                        <Select
                                            value={subcategory}
                                            label="Expense subcategory"
                                            onChange={event =>
                                                setSubcategory(event.target.value)
                                            }
                                        >
                                            {selectedCategory.subcategories.map(item => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                fullWidth
                                label="Merchant"
                                value={merchant}
                                onChange={event => {
                                    const newMerchant = event.target.value;
                                    setMerchant(newMerchant);
                                    if (!description) {
                                        setDescription(suggestDescription(newMerchant, category));
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Amount"
                                type="number"
                                value={amount}
                                inputProps={{ min: 0, step: '0.01' }}
                                onChange={event => setAmount(event.target.value)}
                            />
                            <TextField
                                fullWidth
                                label="Transaction date"
                                type="date"
                                value={transactionDate}
                                InputLabelProps={{ shrink: true }}
                                onChange={event => setTransactionDate(event.target.value)}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Responsible employee</InputLabel>
                                <Select
                                    value={responsibleUserId}
                                    label="Responsible employee"
                                    onChange={event =>
                                        setResponsibleUserId(event.target.value)
                                    }
                                >
                                    {identity?.id != null &&
                                        !users.some(
                                            user =>
                                                String(user.id) ===
                                                String(identity.id)
                                        ) && (
                                            <MenuItem
                                                value={String(identity.id)}
                                            >
                                                Me
                                            </MenuItem>
                                        )}
                                    {users.map(user => (
                                        <MenuItem
                                            key={user.id}
                                            value={String(user.id)}
                                        >
                                            {user.name || user.email}
                                            {identity?.id != null &&
                                            String(user.id) ===
                                                String(identity.id)
                                                ? ' (me)'
                                                : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <TextField
                            fullWidth
                            label="Description (optional)"
                            placeholder="Auto-suggested from merchant or category (max 20 chars)"
                            value={description}
                            onChange={event => setDescription(event.target.value.substring(0, 20))}
                            multiline
                            rows={2}
                            helperText={`${description.length}/20 characters`}
                            variant="outlined"
                        />
                        <Button
                            variant="contained"
                            disabled={disabled}
                            onClick={() => void submit()}
                        >
                            Upload receipt
                        </Button>
                    </Stack>
                </CardContent>
                </Card>
                {uploading && (
                    <Box
                        aria-busy="true"
                        aria-label="Uploading receipt"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            bgcolor: 'rgba(255, 255, 255, 0.72)',
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={36} />
                        <Typography variant="body2" color="text.secondary">
                            Saving receipt…
                        </Typography>
                    </Box>
                )}
            </Box>
            <Typography variant="h6">Recent uploads</Typography>
            <Stack spacing={1}>
                {pendingLocal.map(item => {
                    const isUploading = item.localId === uploadingLocalId;
                    return (
                    <Card key={item.localId} variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography>
                                        {item.original_filename}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {item.allocation_type === 'project_cost'
                                            ? 'Project cost (COGS)'
                                            : item.category}
                                        {item.subcategory
                                            ? `: ${item.subcategory}`
                                            : ''}
                                        {item.project_no != null
                                            ? ` · Project #${item.project_no}`
                                            : ''}
                                        {item.amount
                                            ? ` · $${item.amount}`
                                            : ''}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {isUploading
                                            ? 'Uploading…'
                                            : 'Queued'}{' '}
                                        {formatDate(
                                            new Date(
                                                item.createdAt
                                            ).toISOString(),
                                            'MM-dd-yyyy HH:mm'
                                        )}
                                        {' · '}
                                        Purchase{' '}
                                        {item.transaction_date
                                            ? formatDate(
                                                  item.transaction_date,
                                                  'MM-dd-yyyy'
                                              )
                                            : '—'}
                                        {!isUploading && item.lastError
                                            ? ` · ${item.lastError}`
                                            : ''}
                                    </Typography>
                                </Box>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexShrink={0}
                                >
                                    {isUploading && (
                                        <CircularProgress
                                            size={18}
                                            aria-label="Uploading receipt"
                                        />
                                    )}
                                    <Chip
                                        size="small"
                                        label={
                                            isUploading
                                                ? 'Uploading'
                                                : 'Pending'
                                        }
                                        color="warning"
                                    />
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    );
                })}
                {recent.map(receipt => (
                    <Card key={receipt.id} variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography>{receipt.original_filename}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {receipt.allocation_type === 'project_cost'
                                            ? 'Project cost (COGS)'
                                            : receipt.category}
                                        {receipt.subcategory
                                            ? `: ${receipt.subcategory}`
                                            : ''}
                                        {receipt.project_no != null
                                            ? ` · Project #${receipt.project_no}`
                                            : ''}
                                        {receipt.amount ? ` · $${receipt.amount}` : ''}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Uploaded{' '}
                                        {formatDate(
                                            receipt.created_at,
                                            'MM-dd-yyyy HH:mm'
                                        )}
                                        {' · '}
                                        Purchase{' '}
                                        {receipt.transaction_date
                                            ? formatDate(
                                                  receipt.transaction_date,
                                                  'MM-dd-yyyy'
                                              )
                                            : '—'}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        size="small"
                                        onClick={() => setEditing(receipt)}
                                    >
                                        Edit allocation
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setPreview(receipt)}
                                    >
                                        View
                                    </Button>
                                    <Chip
                                        size="small"
                                        label={receipt.status}
                                        color={statusColor(receipt.status)}
                                    />
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
                {(recent.length > 0 || pendingLocal.length > 0) && (
                    <Box>
                        <Button
                            variant="text"
                            size="small"
                            onClick={onViewMyReceipts}
                            sx={{ px: 0, textTransform: 'none' }}
                        >
                            View more
                        </Button>
                    </Box>
                )}
            </Stack>
            <ReceiptPreviewDialog
                receipt={preview}
                onClose={() => setPreview(null)}
            />
            <ReceiptAllocationEditDialog
                receipt={editing}
                categories={categories}
                users={users}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    void refetchRecent();
                }}
            />
            <ReceiptImageCropDialog
                open={cropSession != null}
                items={cropSession != null ? cropSession.items : EMPTY_CROP_ITEMS}
                onClose={closeCropSession}
                onCropConfirm={cropped => {
                    void handleCropConfirm(cropped);
                }}
            />
        </Stack>
    );
};
